import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { actionItemsService } from '../services/api';
import { ReactComponent as RefreshIcon } from '../assets/refresh.svg';

const ActionItems = () => {
  const navigate = useNavigate();
  const [actionItems, setActionItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const actionItemTypes = [
    { value: 'all', label: 'All Action Items', color: 'bg-blue-100 text-blue-800' },
    { value: 'agent-deprecation', label: 'Agent Deprecation', color: 'bg-orange-100 text-orange-800' }
  ];

  const fetchActionItems = async (type = null) => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (type && type !== 'all') {
        response = await actionItemsService.getActionItemsByType(type);
      } else {
        response = await actionItemsService.getAllActionItems();
      }

      if (response.success) {
        setActionItems(response.data || {});
      } else {
        setError(response.error || 'Failed to fetch action items');
      }
    } catch (err) {
      console.error('Error fetching action items:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch action items');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchActionItems(selectedType === 'all' ? null : selectedType);
    setRefreshing(false);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    fetchActionItems(type === 'all' ? null : type);
  };

  const handleCardClick = (clinicId) => {
    navigate(`/clinics/${clinicId}`);
  };

  useEffect(() => {
    fetchActionItems();
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'ready':
        return 'bg-blue-100 text-blue-800';
      case 'reviewing':
        return 'bg-yellow-100 text-yellow-800';
      case 'setup':
        return 'bg-orange-100 text-orange-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderActionItemCard = (item, type) => (
    <div
      key={`${type}-${item.clid}`}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer hover:border-[#075e54] group"
      onClick={() => handleCardClick(item.clid)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(item.clid);
        }
      }}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#075e54] transition-colors">{item.clinic_name}</h3>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Clinic ID:</span> {item.clid}
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Contact:</span> {item.contact_number}
          </p>
          {item.status && (
            <div className="flex items-center space-x-2 mb-1">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Status:</span>
              </p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
            </div>
          )}
          {item.current_agent_version && (
            <div className="flex items-center space-x-2 mb-1">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Current Agent:</span> {item.current_agent_version}
              </p>
              {item.recommended_agent_version && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  → {item.recommended_agent_version}
                </span>
              )}
            </div>
          )}
          {item.deprecation_reason && (
            <p className="text-sm text-orange-600 mb-1">
              <span className="font-medium">Reason:</span> {item.deprecation_reason}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end space-y-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
            {item.priority || 'Medium'}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionItemTypes.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-800'}`}>
            {actionItemTypes.find(t => t.value === type)?.label || type}
          </span>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-700 mb-2">
          <span className="font-medium">Recommended Action:</span> {item.recommended_action}
        </p>
        {item.prerequisites && item.prerequisites.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-600 mb-1">Prerequisites:</p>
            <div className="flex flex-wrap gap-1">
              {item.prerequisites.map((prereq, index) => (
                <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {prereq}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center text-xs text-gray-500 pt-4 border-t border-gray-100">
        <div className="flex flex-col">
          <span>Created: {formatDate(item.created_at)}</span>
          <span>Updated: {formatDate(item.updated_at)}</span>
        </div>
        <div className="flex items-center text-[#075e54] opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs font-medium mr-1">View Details</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );

  const renderActionItemSection = (type, items) => {
    if (!items || items.length === 0) return null;

    const typeInfo = actionItemTypes.find(t => t.value === type);
    const sectionTitle = typeInfo?.label || type;

    return (
      <div key={type} className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            {sectionTitle}
            <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-normal">
              {items.length}
            </span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(item => renderActionItemCard(item, type))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#075e54]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading action items</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={() => fetchActionItems()}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalItems = Object.values(actionItems).reduce((total, items) => {
    if (Array.isArray(items)) {
      return total + items.length;
    } else if (items && typeof items === 'object' && items.data && Array.isArray(items.data)) {
      return total + items.data.length;
    }
    return total;
  }, 0);

  // Calculate status breakdown
  const getStatusBreakdown = () => {
    const statusCounts = {};
    Object.values(actionItems).forEach(items => {
      const itemsArray = Array.isArray(items) ? items : (items?.data || []);
      itemsArray.forEach(item => {
        if (item.status) {
          statusCounts[item.status] = (statusCounts[item.status] || 0) + 1;
        }
      });
    });
    return statusCounts;
  };

  const statusBreakdown = getStatusBreakdown();
  const criticalStatuses = ['setup', 'reviewing', 'inactive'];
  const criticalCount = criticalStatuses.reduce((count, status) => count + (statusBreakdown[status] || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Action Items</h1>
            <p className="text-gray-600 mt-1">
              Manage and track items that require attention
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-[#075e54] text-white rounded-lg hover:bg-[#064e45] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          {actionItemTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => handleTypeChange(type.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedType === type.value
                  ? 'bg-white text-[#075e54] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Items</p>
              <p className="text-2xl font-semibold text-gray-900">{totalItems}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agent Deprecation</p>
              <p className="text-2xl font-semibold text-gray-900">
                {actionItems['agent-deprecation']?.length || actionItems['agent-deprecation']?.data?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Status Distribution</p>
              <p className="text-2xl font-semibold text-gray-900">
                {criticalCount} / {totalItems}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Needs attention / Total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Items Content */}
      <div className="space-y-8">
        {totalItems === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No action items</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedType === 'all'
                ? 'There are currently no action items that require attention.'
                : `No ${actionItemTypes.find(t => t.value === selectedType)?.label.toLowerCase()} items found.`
              }
            </p>
          </div>
        ) : (
          Object.entries(actionItems).map(([type, items]) => {
            // Handle both direct array and nested object with data array
            const itemsArray = Array.isArray(items) ? items : (items?.data || []);

            // Filter by selected type if not 'all'
            if (selectedType !== 'all' && type !== selectedType) {
              return null;
            }

            return renderActionItemSection(type, itemsArray);
          })
        )}
      </div>
    </div>
  );
};

export default ActionItems;
